'use client';
import React, { useState, useEffect } from 'react';
import { useTargetExam, type ExamCategoryEntry, type ExamGroupEntry, type TargetExam } from '@/contexts/TargetExamContext';

type Step = 'category' | 'group' | 'exam' | 'confirm';

interface PendingExam {
  exam: TargetExam;
  isInList: boolean;
}

export default function ExamPicker() {
  const { categories, targetExam, setTargetExam, closeExamPicker, examPickerMode } = useTargetExam();
  const [step, setStep] = useState<Step>('category');
  const [selectedCategory, setSelectedCategory] = useState<ExamCategoryEntry | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<ExamGroupEntry | null>(null);
  const [pendingExam, setPendingExam] = useState<PendingExam | null>(null);
  const [userExamIds, setUserExamIds] = useState<string[]>([]);
  const [isLoadingExams, setIsLoadingExams] = useState(true);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Load user's existing exams from DB on mount
  useEffect(() => {
    fetch('/api/minikwa/onboarding')
      .then((r) => r.json())
      .then((data) => {
        if (data.userExams && Array.isArray(data.userExams)) {
          const ids: string[] = data.userExams.map((e: { exam_id: string }) => e.exam_id);
          setUserExamIds(ids);
        }
      })
      .catch(() => { /* ignore */ })
      .finally(() => setIsLoadingExams(false));
  }, []);

  function handleBack() {
    if (step === 'confirm') {
      setPendingExam(null);
      setStep('exam');
    } else if (step === 'group') {
      setStep('category');
      setSelectedCategory(null);
    } else if (step === 'exam') {
      setStep('group');
      setSelectedGroup(null);
    }
  }

  function handleSelectExam(exam: TargetExam) {
    const isInList = userExamIds.includes(exam.examId);

    if (examPickerMode === 'add') {
      // Always show 3-button confirmation for add mode
      setPendingExam({ exam, isInList });
      setStep('confirm');
    } else {
      // change mode: if exam is already in list, just set it as target (writes to DB via context);
      // otherwise show 2-button confirmation to add + set target
      if (isInList) {
        handleSetTargetExamInDB(exam);
      } else {
        setPendingExam({ exam, isInList: false });
        setStep('confirm');
      }
    }
  }

  // Write target exam update to DB via global API
  async function handleSetTargetExamInDB(exam: TargetExam) {
    try {
      await fetch('/api/minikwa/onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update_target_exam',
          examId: exam.examId,
          examName: exam.examName,
          examGroup: exam.groupName,
          examEmoji: exam.examEmoji,
        }),
      });
    } catch { /* ignore */ }
    setTargetExam(exam);
    closeExamPicker();
  }

  // Add mode: just add to list without setting as target
  async function handleAddOnly() {
    if (!pendingExam) return;
    try {
      await fetch('/api/minikwa/onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save_step_5',
          exams: [{
            examId: pendingExam.exam.examId,
            examName: pendingExam.exam.examName,
            examGroup: pendingExam.exam.groupName,
            examEmoji: pendingExam.exam.examEmoji,
          }],
        }),
      });
    } catch { /* ignore */ }
    closeExamPicker();
  }

  // Both modes: add to list AND set as target
  async function handleAddAndSetTarget() {
    if (!pendingExam) return;
    await handleSetTargetExamInDB(pendingExam.exam);
  }

  const stepTitle =
    step === 'category' ? 'Select Category' :
    step === 'group' ? 'Select Exam Group' :
    step === 'exam' ? 'Select Exam' :
    examPickerMode === 'add' ? 'Add Exam' : 'Exam not in your list';

  const showBackButton = step !== 'category';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeExamPicker} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl z-10 flex flex-col max-h-[85vh]">
        {/* Drag handle (mobile) */}
        <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mt-3 mb-0 sm:hidden shrink-0" />

        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-slate-100 shrink-0">
          {showBackButton && (
            <button
              onClick={handleBack}
              className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors shrink-0"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-gray-900 text-lg leading-tight">{stepTitle}</h3>
            {step !== 'confirm' && (
              <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-400">
                <span className={step === 'category' ? 'text-navy font-semibold' : ''}>Category</span>
                <span>›</span>
                <span className={step === 'group' ? 'text-navy font-semibold' : step === 'exam' ? '' : 'opacity-40'}>Group</span>
                <span>›</span>
                <span className={step === 'exam' ? 'text-navy font-semibold' : 'opacity-40'}>Exam</span>
              </div>
            )}
            {step === 'confirm' && (
              <p className="text-xs text-gray-400 mt-0.5">
                {examPickerMode === 'add' ? 'Choose how to add this exam' : 'Add to continue'}
              </p>
            )}
          </div>
          <button
            onClick={closeExamPicker}
            className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors shrink-0"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-4 py-3">

          {/* Step 1: Categories */}
          {step === 'category' && (
            <div className="flex flex-col gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => { setSelectedCategory(cat); setStep('group'); }}
                  className="flex items-center gap-3 w-full text-left bg-slate-50 hover:bg-navy/5 border border-slate-200 hover:border-navy/20 rounded-2xl px-4 py-3.5 transition-all group"
                >
                  <span className="text-2xl w-9 text-center shrink-0">{cat.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">{cat.name}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{cat.groups.length} exam group{cat.groups.length !== 1 ? 's' : ''}</p>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 group-hover:text-navy transition-colors shrink-0">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Groups */}
          {step === 'group' && selectedCategory && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide px-1 mb-1">{selectedCategory.name}</p>
              {selectedCategory.groups.map((grp) => (
                <button
                  key={grp.id}
                  onClick={() => { setSelectedGroup(grp); setStep('exam'); }}
                  className="flex items-center gap-3 w-full text-left bg-slate-50 hover:bg-navy/5 border border-slate-200 hover:border-navy/20 rounded-2xl px-4 py-3.5 transition-all group"
                >
                  <span className="text-2xl w-9 text-center shrink-0">{grp.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">{grp.name}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{grp.exams.length} exam{grp.exams.length !== 1 ? 's' : ''}</p>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 group-hover:text-navy transition-colors shrink-0">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              ))}
            </div>
          )}

          {/* Step 3: Exams */}
          {step === 'exam' && selectedGroup && selectedCategory && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide px-1 mb-1">{selectedGroup.name}</p>
              {isLoadingExams ? (
                <div className="text-center py-6">
                  <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-xs text-gray-400">Loading your exams...</p>
                </div>
              ) : selectedGroup.exams.map((exam) => {
                const isSelected = targetExam?.examId === exam.id;
                const alreadyInList = userExamIds.includes(exam.id);
                return (
                  <button
                    key={exam.id}
                    onClick={() =>
                      handleSelectExam({
                        examId: exam.id,
                        examName: exam.name,
                        examEmoji: exam.emoji,
                        groupId: selectedGroup.id,
                        groupName: selectedGroup.name,
                        categoryId: selectedCategory.id,
                        categoryName: selectedCategory.name,
                      })
                    }
                    className={`flex items-center gap-3 w-full text-left border rounded-2xl px-4 py-3.5 transition-all group ${
                      isSelected
                        ? 'bg-navy/5 border-navy/30' :'bg-slate-50 hover:bg-navy/5 border-slate-200 hover:border-navy/20'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-navy/10 flex items-center justify-center shrink-0">
                      <span className="text-base">{exam.emoji}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm">{exam.name}</p>
                      <p className="text-gray-400 text-xs mt-0.5">
                        {selectedGroup.name}
                        {alreadyInList && !isSelected && (
                          <span className="ml-2 text-emerald-600 font-semibold">· In your list</span>
                        )}
                      </p>
                    </div>
                    {isSelected ? (
                      <div className="shrink-0 w-6 h-6 rounded-full bg-navy flex items-center justify-center">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    ) : (
                      <div className="shrink-0 px-3 py-1.5 bg-navy text-white rounded-xl text-xs font-bold group-hover:bg-navy-light transition-colors">
                        Select
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 'confirm' && pendingExam && (
            <div className="py-2">
              {/* Icon + title */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1B4FD8" strokeWidth="2">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    <line x1="12" y1="11" x2="12" y2="17" />
                    <line x1="9" y1="14" x2="15" y2="14" />
                  </svg>
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-base">
                    {examPickerMode === 'add' ? 'Add exam to your list?' : 'Exam not in your list'}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {examPickerMode === 'add' ? 'Choose how to add this exam' : 'Add to continue'}
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                <span className="font-semibold text-gray-900">{pendingExam.exam.examName}</span>
                {examPickerMode === 'add' ?' is not part of your exams yet. Would you like to add it, or add it and set it as your target exam?' :' is not part of your exams. Would you like to add it and set it as your target exam?'}
              </p>

              {/* Buttons */}
              {examPickerMode === 'add' ? (
                /* 3-button layout for add mode */
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <button
                      onClick={handleAddOnly}
                      className="flex-1 py-3 border-2 border-navy text-navy rounded-2xl text-sm font-bold hover:bg-navy/5 transition-colors"
                    >
                      Add Exam
                    </button>
                    <button
                      onClick={handleAddAndSetTarget}
                      className="flex-1 py-3 bg-[#2D5A1B] text-white rounded-2xl text-sm font-bold hover:bg-[#3a7022] transition-colors"
                    >
                      Add &amp; Set as Target
                    </button>
                  </div>
                  <button
                    onClick={closeExamPicker}
                    className="w-full py-3 border border-gray-200 text-gray-500 rounded-2xl text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                /* 2-button layout for change mode */
                <div className="flex gap-2">
                  <button
                    onClick={closeExamPicker}
                    className="flex-1 py-3 border border-gray-200 text-gray-600 rounded-2xl text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddAndSetTarget}
                    className="flex-1 py-3 bg-[#2D5A1B] text-white rounded-2xl text-sm font-bold hover:bg-[#3a7022] transition-colors"
                  >
                    Add &amp; Set as Target
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="h-4 shrink-0" />
      </div>
    </div>
  );
}
